import { PropType, computed, defineComponent, onUnmounted, ref, watch } from 'vue'
import { Store } from 'pinia'
import { showToast, closeToast } from '@nio-fe/horo-h5'
import { gcj02ToBd09 } from '@/utils/coordinateTransform'
import { cur } from '@/utils/env'
import Tracker from '@nio-fe/nio-tracker-web'
import { ChargeMapContext } from '../../chargerMap/index.model'
import { ComponentActions, ComponentState } from '../../../nsh5/localStores/component'
import { useAppStore } from '../../../stores/app'
import { MapViewMode, Point } from '../../../contract/map'
import { flyTo } from '../../chargerMap/mapUtils'
import { DEFAULT_POSITION } from '../../../config/defaultPosition'
import { DEFAULT_CITY_ZOOM_LEVEL } from '../../../config/zoomLevelConfig'
import { useRouter } from 'vue-router'
import PositionIcon from './positionIcon'

/**
 * 戳点渲染功能模块
 */
export default defineComponent({
  name: 'position-render-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    BMapGL: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
  },

  setup: (props, { expose }) => {
    const mapContextRef = computed(() => props.mapContextRef)
    const mapInstance = computed(() => props.mapInstance)
    const BMapGL = computed(() => props.BMapGL)
    const router = useRouter()
    const zoomLevel = ref<number>(0)
    let positionMarker = null as any // 定位
    const appStore = useAppStore()

    let myLocation: Point | null = null // gcj

    // 定位
    const handleLocation = () => {
      if (props.componentStore?.viewMode === MapViewMode.CountyLayout) {
        // 县县通模式不需要回到首页
      } else {
        router.replace('/')
      }
      // props.componentStore.bus.emit('resetClickedMarker') // 切换tab还原点击的marker
      myLocation = props.componentStore.myLocation as Point
      if (myLocation) {
        // 有缓存走缓存
        flyTo(mapContextRef.value!, gcj02ToBd09(myLocation!), 16)
        setPositionMarker(gcj02ToBd09(myLocation!))
      } else {
        showToast({
          content: '正在定位...',
          duration: 30000,
        })
        mapContextRef.value
          ?.getCurrentPosition()
          .then((loc: Point) => {
            myLocation = loc
          })
          .catch(async () => {
            // 未获取到定位采取默认定位
            myLocation = DEFAULT_POSITION
            appStore.$patch({
              locationSuccess: false,
            })
          })
          .finally(() => {
            closeToast()
            flyTo(mapContextRef.value!, gcj02ToBd09(myLocation!), 16)
            setPositionMarker(gcj02ToBd09(myLocation!))
          })
      }
      // 定位点击埋点  城市改动写入到appStore.cityLocationClick
      const locCity = {
        cityname: props.componentStore.myLocation?.name,
        locationsuccess: appStore.locationSuccess,
      }
      props.componentStore.bus.emit('hideSummary')
      Tracker.track('nsh5_citylocationclick', Object.assign(locCity, appStore.trackParamsCommon))
      Tracker.track('h5_page_tracker', {
        channel: window.channel,
        client: window.client,
        brand: (process.config as any).brand,
        screen_orientation: window.screen_orientation,
        view_mode: props.componentStore.viewMode,
        container: window.container,
        user_identity: window.user_identity,
        trigger: 'click',
        element: '定位按钮',
        page_source: window.page_source,
      })
    }

    props.componentStore.bus.on('handleLocation', handleLocation)

    const setPositionMarker = (loc: Point) => {
      // bd09
      // 设置定位点
      clearPositionMarker()
      try {
        const myIcon = new BMapGL.value!.Icon(
          `https://cdn-power-public${cur}.nio.com/pe-fe-statics/pe/ns/h5/position-s.svg`,
          new BMapGL.value!.Size(40, 40)
        )
        const point = new BMapGL.value!.Point(loc.lng, loc.lat)
        const markerPoint = new BMapGL.value!.Marker(point, {
          icon: myIcon,
          enableDragging: false,
          offset: new BMapGL.value!.Size(0, 0),
        })
        markerPoint.disableMassClear()
        markerPoint.setZIndex(10)
        mapInstance.value?.addOverlay(markerPoint)
        positionMarker = markerPoint
      } catch (error) {
        console.log(`================出错了: ${error}====================`)
      }
    }

    props.componentStore.bus.on('clearPositionMarker', () => {
      clearPositionMarker()
    })
    /**
     * 清除定位点
     */
    const clearPositionMarker = () => {
      // 清除POI点
      mapInstance.value?.removeOverlay(positionMarker)
      positionMarker = null
    }

    // ------------暴露出去的方法--------------
    const zoomEnd = async () => {
      zoomLevel.value = mapInstance.value?.getZoom()
    }

    expose({
      zoomEnd,
    })

    // 启动相关事件监听
    const initEventListener = () => {
      mapInstance.value?.addEventListener('zoomend', zoomEnd, false)
    }

    onUnmounted(() => {
      clearPositionMarker()
      props.componentStore.bus.off('clearPositionMarker')
      props.componentStore.bus.off('handleLocation')
    })

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = () => {
      initEventListener()
    }
    watch(
      () => props.mapContextRef,
      () => {
        if (props.mapContextRef) {
          onReady()
        }
      }
    )
    watch(
      () => zoomLevel.value,
      () => {
        if (zoomLevel.value <= DEFAULT_CITY_ZOOM_LEVEL) {
          clearPositionMarker()
        }
      }
    )
    watch(
      () => props.componentStore.viewMode,
      () => {
        if (
          props.componentStore.viewMode !== MapViewMode.Init &&
          props.componentStore.viewMode !== MapViewMode.WishPower
        ) {
          clearPositionMarker()
        }
      }
    )
    return () => <PositionIcon onHandleLocation={handleLocation}></PositionIcon>
  },
})
